import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { IndividualStudentReportOverallQuery } from '../../graphQLOperations/__generated__/IndividualStudentReportOverallQuery.graphql';
import { FC } from 'react';
import { getOverallProjection } from '../../graphQLOperations/IndividualStudentReport';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';

interface Props {
  queryReferenceProjection: PreloadedQuery<IndividualStudentReportOverallQuery, Record<string, unknown>>;
  studentFirstName: string;
  publicUserId: string;
  id: string;
  currentRoadmap?: { id: string; name?: string | null } | null;
}

export const IndividualReportProjectionMsg: FC<Props> = ({
  queryReferenceProjection,
  studentFirstName,
  currentRoadmap,
  publicUserId,
}) => {
  const { IndividualReportOverview } = usePreloadedQuery<IndividualStudentReportOverallQuery>(
    getOverallProjection,
    queryReferenceProjection,
  );

  const remainingSkills =
    IndividualReportOverview.roadmapSkills -
    (IndividualReportOverview.demonstratedSkillsInDiagnostic +
      IndividualReportOverview.demonstratedSkillsSinceDiagnostic);

  const componentVariants = {
    atrisk_high: (
      <div className="nc_individual-report-projected-container tw-w-full tw-grid tw-grid-flow-col tw-grid-rows-1 tw-gap-4">
        <div className="tw-row-span-1">
          <div className="tw-bg-[#FFF5EB] sm:tw-w-[150px] lg:tw-w-[256px] tw-border-[#D69C0E] tw-rounded-md tw-flex tw-justify-center tw-items-center tw-border-[1px] tw-border-solid">
            <div className="tw-py-4">
              <p className="tw-text-center tw-text-lg tw-mb-0">
                <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
                  {studentFirstName}
                </MaskedStudentData>{' '}
                is:
              </p>
              <p className="tw-text-center tw-flex tw-items-center tw-justify-center">
                <span className="tw-font-semibold tw-text-lg">Approaching</span>
              </p>
            </div>
          </div>
        </div>
        <div className="tw-row-span-1 tw-col-span-1">
          <h4>Projected Performance</h4>
          <p className="tw-font-semibold">
            Based on{' '}
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
              {studentFirstName}
            </MaskedStudentData>
            {"'"}s current progress, they are projected to complete{' '}
            {IndividualReportOverview.projectedPercentageCurrentPace}% of their grade-level roadmap by the end of the
            school year.
          </p>
          <p className="tw-font-semibold">
            Students are typically expected to complete at least 75% of their grade-level skills by year{"'"}s end to be
            on track and{' '}
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
              {studentFirstName}
            </MaskedStudentData>
            {"'"}s projected completion is below this threshold.
          </p>
        </div>
        <div className="tw-row-span-1 tw-col-span-1 tw-pt-6">
          <p className="tw-text-neutral-800 tw-pr-5">
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
              {studentFirstName}
            </MaskedStudentData>
            {"'"}s current learning pace is slightly below the recommended average of 2 skills per week. Increasing this
            pace while maintaining consistency can help sustain momentum toward a strong end-of-year Roadmap completion.
          </p>
        </div>
      </div>
    ),
    atrisk_low: (
      <div className="nc_individual-report-projected-container tw-w-full tw-grid tw-grid-flow-col tw-grid-rows-1 tw-gap-4">
        <div className="tw-row-span-1">
          <div className="tw-bg-[#FFF5EB] sm:tw-w-[150px] lg:tw-w-[256px] tw-border-[#D69C0E] tw-rounded-md tw-flex tw-justify-center tw-items-center tw-border-[1px] tw-border-solid">
            <div className="tw-py-4">
              <p className="tw-text-center tw-text-lg tw-mb-0">
                <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
                  {studentFirstName}
                </MaskedStudentData>{' '}
                is:
              </p>
              <p className="tw-text-center tw-flex tw-items-center tw-justify-center">
                <span className="tw-font-semibold tw-text-lg">Approaching</span>
              </p>
            </div>
          </div>
        </div>

        <div className="tw-row-span-1 tw-col-span-1">
          <h4>Projected Performance</h4>
          <p className="tw-font-semibold">
            Based on their current progress,{' '}
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
              {studentFirstName}
            </MaskedStudentData>{' '}
            is projected to complete less than 75% of their grade-level roadmap by the end of the school year, which
            falls below the threshold for being on track for grade-level success.
          </p>
          <p className="tw-text-neutral-800 tw-pr-5 ">
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
              {studentFirstName}
            </MaskedStudentData>
            {"'"}s current pace is not sufficient to close the gap toward this goal. Increasing their pace to 2 or more
            skills per week would significantly improve their projected completion. The table below shows estimated
            Roadmap completion at different learning paces.
          </p>
        </div>

        <div className="tw-pt-6 tw-w-[25rem] tw-mt-4">
          <DataTable
            showGridlines
            value={[
              {
                name: <span className="tw-font-bold">{IndividualReportOverview.currentPace + ' (current pace)'}</span>,
                percentage: IndividualReportOverview.projectedPercentageCurrentPace + '%',
              },
              {
                name: <span className="tw-font-bold">2</span>,
                percentage: IndividualReportOverview.projectedPercentageTwoSkillsPace + '%',
              },
              {
                name: <span className="tw-font-bold">3</span>,
                percentage: IndividualReportOverview.projectedPercentageThreeSkillsPace + '%',
              },
              {
                name: <span className="tw-font-bold">4</span>,
                percentage: IndividualReportOverview.projectedPercentageFourSkillsPace + '%',
              },
            ]}
          >
            <Column className="tw-text-center" alignHeader="center" header={'Skills a week'} field="name" />
            <Column className="tw-text-center" alignHeader="center" header={'Completion'} field="percentage" />
          </DataTable>
        </div>
      </div>
    ),
    ontrack_high: (
      <div className="nc_individual-report-projected-container tw-w-full tw-grid tw-grid-flow-col tw-grid-rows-1 tw-gap-4">
        <div className="tw-row-span-1">
          <div className="tw-bg-[#DAF6FE] tw-border-[#3FDEFB] sm:tw-w-[150px] lg:tw-w-[256px] tw-rounded-md tw-flex tw-justify-center tw-items-center tw-border-[1px] tw-border-solid">
            <div className="tw-py-4">
              <p className="tw-text-center tw-text-lg tw-mb-0">
                <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
                  {studentFirstName}
                </MaskedStudentData>{' '}
                is:
              </p>
              <p className="tw-text-center tw-flex tw-items-center tw-justify-center">
                <span className="tw-font-semibold tw-text-lg">On Track</span>
              </p>
            </div>
          </div>
        </div>
        <div className="tw-row-span-1 tw-col-span-1">
          <h4>Projected Performance</h4>
          <p className="tw-font-semibold">
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
              {studentFirstName}
            </MaskedStudentData>{' '}
            is making steady progress towards end-of-year goals.{' '}
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
              {studentFirstName}
            </MaskedStudentData>{' '}
            currently has a learning pace of {IndividualReportOverview.currentPace}, which is meeting or exceeding our
            recommended learning pace. Keep it up,{' '}
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
              {studentFirstName}
            </MaskedStudentData>
            !
          </p>
        </div>
        <div className="tw-row-span-1 tw-col-span-1 tw-pt-6">
          <p className="tw-text-neutral-800 tw-pr-5">
            Overall,{' '}
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
              {studentFirstName}
            </MaskedStudentData>{' '}
            has <span>{remainingSkills} skills remaining</span> to complete their {currentRoadmap?.name} Roadmap. At
            current pace{' '}
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
              {studentFirstName}
            </MaskedStudentData>{' '}
            is on track to completing at least 75% of their Roadmap, building critical knowledge and confidence in
            grade-level skills.
          </p>
        </div>
      </div>
    ),
    ontrack_low: (
      <div className="nc_individual-report-projected-container tw-w-full tw-grid tw-grid-flow-col tw-grid-rows-1 tw-gap-4">
        <div className="tw-row-span-1">
          <div className="tw-bg-[#DAF6FE] tw-border-[#3FDEFB] sm:tw-w-[150px] lg:tw-w-[256px] tw-rounded-md tw-flex tw-justify-center tw-items-center tw-border-[1px] tw-border-solid">
            <div className="tw-py-4">
              <p className="tw-text-center tw-text-lg tw-mb-0">
                <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
                  {studentFirstName}
                </MaskedStudentData>{' '}
                is:
              </p>
              <p className="tw-text-center tw-flex tw-items-center tw-justify-center">
                <span className="tw-font-semibold tw-text-lg">On Track</span>
              </p>
            </div>
          </div>
        </div>
        <div className="tw-row-span-1 tw-col-span-1">
          <h4>Projected Performance</h4>
          <p className="tw-font-semibold">
            Based on their current progress,{' '}
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FirstName}>
              {studentFirstName}
            </MaskedStudentData>{' '}
            is projected to complete at least 75% of their grade-level roadmap by the end of the school year.
          </p>
        </div>
        <div className="tw-row-span-1 tw-col-span-1 tw-pt-6">
          <p className="tw-text-neutral-800 tw-pr-5">
            While their current learning pace is slightly below the recommended average of 2 skills per week,
            maintaining steady engagement can help support continued progress toward year-end goals.
          </p>
        </div>
      </div>
    ),
  };
  const getComponentVariants = () => {
    // `AHEAD` is no longer surfaced as its own status: it renders as On Track.
    if (
      IndividualReportOverview.projectedStatusCurrentPace === 'AHEAD' ||
      IndividualReportOverview.projectedStatusCurrentPace === 'ON_TRACK'
    ) {
      if (IndividualReportOverview.currentPace < 2) {
        return componentVariants.ontrack_low;
      } else {
        return componentVariants.ontrack_high;
      }
    }
    if (IndividualReportOverview.projectedStatusCurrentPace === 'AT_RISK') {
      if (IndividualReportOverview.currentPace < 2) {
        return componentVariants.atrisk_low;
      } else {
        return componentVariants.atrisk_high;
      }
    }
  };
  return <div>{getComponentVariants()}</div>;
};
